/**
 * Content Loader for HUL Proposal Website
 * Fetches proposal data from CloudFront/S3 or uses mock data in development
 */

import type { HULProposalContent, ProposalLoadingState } from '../types';
import { mockProposalData, mockServiceProposalData } from '../data/mockProposal';

const CLOUDFRONT_BASE_URL = import.meta.env.VITE_CLOUDFRONT_URL || '';
// Only use mock data if explicitly set to 'true' - production serves from same domain
const USE_MOCK_DATA = import.meta.env.VITE_USE_MOCK_DATA === 'true';
// Use service proposal mock data for testing (set VITE_MOCK_SERVICE=true)
const USE_SERVICE_MOCK = import.meta.env.VITE_MOCK_SERVICE === 'true';

/**
 * Extract proposal ID from URL path
 * Expected format: /proposal/{proposalId} or /{proposalId}
 */
export function getProposalIdFromUrl(): string | null {
  const path = window.location.pathname;

  console.log('[ContentLoader] Pathname:', path);

  // Match /proposal/{id}/ or /{id}/ (with optional trailing slash)
  const match = path.match(/\/(?:proposal\/)?([A-Za-z0-9-]+)\/?$/);

  console.log('[ContentLoader] Regex match:', match);

  if (match) {
    console.log('[ContentLoader] Extracted proposalId:', match[1]);
    return match[1];
  }

  // Also check query params as fallback
  const params = new URLSearchParams(window.location.search);
  const fallbackId = params.get('id') || params.get('proposalId');
  console.log('[ContentLoader] Fallback proposalId:', fallbackId);
  return fallbackId;
}

/**
 * Check if proposal has expired
 */
export function isProposalExpired(validUntil: string): boolean {
  const expiryDate = new Date(validUntil);
  const now = new Date();
  return now > expiryDate;
}

/**
 * Calculate days until expiration
 */
export function getDaysUntilExpiry(validUntil: string): number {
  const expiryDate = new Date(validUntil);
  const now = new Date();
  const diffTime = expiryDate.getTime() - now.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Normalize task data to ensure consistent field names
 * Handles migration from 'amount' to 'price' field
 */
function normalizeProposalData(data: HULProposalContent): HULProposalContent {
  // Normalize tasks if present (service proposals)
  if (data.tasks && Array.isArray(data.tasks)) {
    data.tasks = data.tasks.map((task, idx) => ({
      ...task,
      // Ensure lineId exists
      lineId: task.lineId || String(idx + 1),
      // Ensure price exists (fall back to amount for backwards compatibility)
      price: task.price ?? (task as unknown as { amount?: number }).amount ?? 0,
    }));
  }
  return data;
}

/**
 * Fetch proposal content from CloudFront
 */
async function fetchFromCloudFront(proposalId: string): Promise<HULProposalContent> {
  // Content is stored at /{proposalId}/content.json (no /proposals/ prefix)
  const url = CLOUDFRONT_BASE_URL
    ? `${CLOUDFRONT_BASE_URL}/${proposalId}/content.json`
    : `/${proposalId}/content.json`;

  console.log('[ContentLoader] Fetching:', url);

  const response = await fetch(url, {
    headers: {
      'Accept': 'application/json',
    },
  });

  console.log('[ContentLoader] Response status:', response.status, response.statusText);
  console.log('[ContentLoader] Content-Type:', response.headers.get('content-type'));

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Proposal not found');
    }
    throw new Error(`Failed to load proposal: ${response.statusText}`);
  }

  const text = await response.text();
  console.log('[ContentLoader] Response preview:', text.substring(0, 100));

  try {
    return JSON.parse(text);
  } catch (e) {
    console.error('[ContentLoader] JSON parse error:', e);
    console.error('[ContentLoader] Full response:', text.substring(0, 500));
    throw e;
  }
}

/**
 * Load proposal content
 * Uses mock data in development, CloudFront in production
 */
export async function loadProposalContent(
  proposalId: string | null
): Promise<ProposalLoadingState> {
  // No proposal ID provided
  if (!proposalId) {
    return {
      status: 'error',
      error: 'No proposal ID provided. Please check the URL.',
    };
  }

  try {
    let data: HULProposalContent;

    if (USE_MOCK_DATA) {
      // Simulate network delay in dev
      await new Promise(resolve => setTimeout(resolve, 500));
      // Use service mock if VITE_MOCK_SERVICE=true, otherwise equipment mock
      data = USE_SERVICE_MOCK ? mockServiceProposalData : mockProposalData;
    } else {
      data = await fetchFromCloudFront(proposalId);
    }

    // Normalize data to ensure consistent field names
    data = normalizeProposalData(data);

    // Check if password protected (password required but not verified)
    if (data.proposal.password) {
      return { status: 'protected' };
    }

    // Check expiration
    if (isProposalExpired(data.proposal.validUntil)) {
      return {
        status: 'expired',
        validUntil: data.proposal.validUntil,
      };
    }

    return {
      status: 'loaded',
      data,
    };
  } catch (error) {
    return {
      status: 'error',
      error: error instanceof Error ? error.message : 'Failed to load proposal',
    };
  }
}

/**
 * Verify proposal password
 */
export async function verifyPassword(
  proposalId: string,
  password: string
): Promise<{ valid: boolean; data?: HULProposalContent }> {
  try {
    let data: HULProposalContent;

    if (USE_MOCK_DATA) {
      await new Promise(resolve => setTimeout(resolve, 300));
      data = USE_SERVICE_MOCK ? mockServiceProposalData : mockProposalData;
    } else {
      data = await fetchFromCloudFront(proposalId);
    }

    // Normalize data to ensure consistent field names
    data = normalizeProposalData(data);

    // Simple password check (in production, this should be server-side)
    const isValid = data.proposal.password === password;

    if (isValid) {
      return { valid: true, data };
    }

    return { valid: false };
  } catch {
    return { valid: false };
  }
}

/**
 * Format the valid until date for display
 */
export function formatValidUntil(validUntil: string): string {
  const date = new Date(validUntil);
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}
